/**
 * The survivor figure — the body a player is drawn as.
 *
 * One builder, two callers: a teammate's avatar and, since the view pulls back
 * for hiding places and ladders, your own. The alternative was a second copy of
 * the same capsules in Game, which is exactly how the torch geometry started
 * before the two copies drifted apart and one of them ended up lighting the
 * scene from the wrong place. A figure you see over your own shoulder must be
 * the figure everyone else sees, or the game is lying to somebody.
 */

import * as THREE from 'three';

export interface SurvivorFigure {
  /** Everything physical, ready to hang off an avatar's anchor. */
  readonly group: THREE.Group;
  /** Kept so the crouch can be undone on the head alone. */
  readonly head: THREE.Mesh;
}

/**
 * A simple figure — legible as human at a glance, invisible in fog anyway.
 *
 * Materials are built per figure rather than shared module-wide, so disposing
 * one avatar can never blank out the rest of them.
 */
export function makeSurvivorFigure(): SurvivorFigure {
  const group = new THREE.Group();

  const skin = new THREE.MeshLambertMaterial({ color: 0x8a7d6b });
  const clothes = new THREE.MeshLambertMaterial({ color: 0x3d4a52 });

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.3, 0.85, 4, 8), clothes);
  body.position.y = 1.0;
  body.castShadow = true;
  group.add(body);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), skin);
  head.position.y = 1.72;
  head.castShadow = true;
  group.add(head);

  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.11, 0.6, 3, 6), clothes);
    leg.position.set(side * 0.16, 0.4, 0);
    group.add(leg);
  }

  return { group, head };
}

/**
 * Duck the whole figure, as a fraction of standing height.
 *
 * Scaling from the feet drops the head, the shoulders and anything parented
 * alongside together — squashing the torso alone left the head floating at full
 * height, which read as a bug rather than as ducking. The head is counter-
 * scaled here rather than at each call site, because a person ducks and their
 * skull does not flatten, and two call sites would eventually disagree.
 */
export function poseSurvivorFigure(figure: SurvivorFigure, crouch: number): void {
  figure.group.scale.y = crouch;
  figure.head.scale.y = 1 / crouch;
}

/** Release the geometry and materials this figure owns. */
export function disposeSurvivorFigure(figure: SurvivorFigure): void {
  figure.group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry.dispose();
    const material = object.material;
    if (Array.isArray(material)) for (const m of material) m.dispose();
    else material.dispose();
  });
}
